import NoSleep from 'nosleep.js'
import { useEffect } from 'react'

const isExpectedNoSleepError = (error: unknown): boolean => {
  if (!(error instanceof DOMException)) {
    return false
  }

  const message = `${error.message}`.toLowerCase()
  const isExpectedVisibilityError =
    error.name === 'NotAllowedError' && message.includes('not visible')
  const isExpectedAbortError =
    error.name === 'AbortError' &&
    (message.includes('play() request was interrupted') ||
      message.includes('removed from the document'))

  return isExpectedVisibilityError || isExpectedAbortError
}

const NoSleepManager = () => {
  useEffect(() => {
    const noSleep = new NoSleep()
    const originalEnable = noSleep.enable.bind(noSleep)
    let enableInFlight: Promise<unknown> | null = null
    let disableAfterEnable = false

    const disableNoSleepIfEnabled = () => {
      if (noSleep.isEnabled) {
        noSleep.disable()
      }
    }

    const safeEnableNoSleep = () => {
      if (document.visibilityState !== 'visible') {
        return Promise.resolve()
      }

      if (enableInFlight) {
        return enableInFlight
      }

      enableInFlight = originalEnable()
        .catch((error: unknown) => {
          if (!isExpectedNoSleepError(error)) {
            console.error('NoSleep enable failed:', error)
          }
        })
        .finally(() => {
          enableInFlight = null

          if (disableAfterEnable) {
            disableNoSleepIfEnabled()
          }
        })

      return enableInFlight
    }

    // NoSleep internally retries on visibility changes; patching `enable` ensures those retries are safe too.
    noSleep.enable = safeEnableNoSleep

    const onFirstInteraction = () => {
      document.removeEventListener('click', onFirstInteraction, false)
      void safeEnableNoSleep()
    }

    document.addEventListener('click', onFirstInteraction, false)

    return () => {
      document.removeEventListener('click', onFirstInteraction, false)

      if (enableInFlight) {
        disableAfterEnable = true
        return
      }

      disableNoSleepIfEnabled()
    }
  }, [])

  return null
}

export default NoSleepManager
